import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from "@ngrx/store";
import { MutationOptionsAlone } from 'apollo-angular/types';
import { of } from "rxjs";
import { catchError, concatMap, map, tap, mergeMap } from "rxjs/operators";

import { generateMutationContext } from "src/app/core/auth/session";
import { DetailsHelperService } from "src/app/services/details-helper.service";
import { DocumentHelperService } from "src/app/services/document-helper.service";
import { FreyaHelperService } from "src/app/services/freya-helper.service";
import { FreyaMutateService } from "src/app/services/freya-mutate.service";

import { UpdateJobGQL, UpdateJobMutationVariables, UpdateJobMutation } from "../../../generated/graphql.generated";
import {
  jobToolFeature,
  selectJob,
  selectTransactions,
  selectJobId,
  selectJobFranchiseId,
} from "../job-tool.reducer";

import { JobFinancialsActions } from "./job-financials.actions";

export const openCreateInvoiceDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  documentHelper = inject(DocumentHelperService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.addInvoiceButtonClicked),
      concatLatestFrom(() => store.select(selectJob)),
      tap(([ _action, job ]) => documentHelper.openCreateInvoiceDialog(job?.id)),
    );
}, { functional: true, dispatch: false });

export const openCreateDamageDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  mutateService = inject(FreyaMutateService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.addDamageButtonClicked),
      concatLatestFrom(() => store.select(selectJob)),
      tap(([ _action, job ]) => mutateService.openMutateObject({
        objectType: 'charge',
        mutateType: 'create',
        additionalValues: [
          {
            property: 'job',
            value: job,
          },
          {
            property: 'isDamage',
            value: true,
          }
        ],
      })),
    );
}, { functional: true, dispatch: false });

export const openInvoiceDetails = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.transactionInvoiceTagClicked),
      tap(({ invoiceId }) => detailsHelper.detailsItem.next({
        type: 'invoice',
        item: {
          id: invoiceId,
        },
      })),
    );
}, { functional: true, dispatch: false });

export const openTransactionDetails = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.transactionRowClicked),
      tap(({ transactionId }) => {
        detailsHelper.open(
          'transaction',
          { id: transactionId },
          false,
        );
      }),
    );
}, { functional: true, dispatch: false });

export const openEditTransactionDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  mutateService = inject(FreyaMutateService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.editTransactionMenuItemClicked),
      concatLatestFrom(() => [
        store.select(selectTransactions),
        store.select(jobToolFeature.selectActionsMenuTransactionsId),
      ]),
      tap(([ _action, transactions, transactionId ]) => {

        const selectedTransaction = transactions.find((c) => c.id === transactionId);

        if (!selectedTransaction) { return; }

        mutateService.openMutateObject({
          objectType: 'transaction',
          mutateType: 'update',
          object: selectedTransaction,
        });

      }),
    );
}, { functional: true, dispatch: false });

export const viewReceipt = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  freyaHelper = inject(FreyaHelperService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.viewReceiptMenuItemClicked),
      concatLatestFrom(() => [
        store.select(selectTransactions),
        store.select(jobToolFeature.selectActionsMenuTransactionsId),
        store.select(selectJob),
      ]),
      tap(([ _action, transactions, transactionId, { artifacts } ]) => {

        const selectedTransaction = transactions.find((c) => c.id === transactionId);

        if (!selectedTransaction) { return; }

        const artifactReceiptId = selectedTransaction.metadata?.artifactReceiptId;

        const receipt = artifacts.find((a) => a.id === artifactReceiptId);

        freyaHelper.openInDialog(receipt);
      }),
    );
}, { functional: true, dispatch: false });

export const openDeleteTransactionDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  mutateService = inject(FreyaMutateService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.deleteTransactionMenuItemClicked),
      concatLatestFrom(() => [
        store.select(selectJob),
        store.select(selectTransactions),
        store.select(jobToolFeature.selectActionsMenuTransactionsId),
      ]),
      tap(([ _action, job, transactions, transactionId ]) => {

        const selectedTransaction = transactions.find((c) => c.id === transactionId);

        if (!selectedTransaction) { return; }

        mutateService.openDeleteObject({
          objectId: transactionId,
          objectName: `this ${selectedTransaction.type}`,
          objectType: 'transaction',
          customTitle: 'Delete Transaction',
          additionalValues: [
            {
              property: 'jobId',
              value: job.id,
            },
          ],
        });

      }),
    );

  }, { functional: true, dispatch: false });

export const openEditDamageDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  mutateService = inject(FreyaMutateService),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.editDamageMenuItemClicked),
      concatLatestFrom(() => [
        store.select(selectJob),
        store.select(jobToolFeature.selectActionsMenuDamageId),
      ]),
      tap(([ _action, job, damageId ]) => {

        const selectedDamage = job.charges.find((c) => c.id === damageId);

        if (!selectedDamage) { return; }

        mutateService.openMutateObject({
          mutateType: 'update',
          objectType: 'charge',
          object: selectedDamage,
          additionalValues: [
            {
              property: 'job',
              value: job,
            },
            {
              property: 'isDamage',
              value: true,
            },
          ],
        });

      }),
    );
}, { functional: true, dispatch: false });

export const removeDamage = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(JobFinancialsActions.deleteDamageMenuItemClicked),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJobId),
          store.select(jobToolFeature.selectActionsMenuDamageId),
          store.select(selectJobFranchiseId),
        ]),
        map(([ _action, jobId, chargeId, jobFranchiseId ]) => {
          const vars: UpdateJobMutationVariables = {
            updateJobs: [{
              jobId,
              removeCharges: [chargeId],
            }],
          };
          const opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables> = {
            context: generateMutationContext({
              zone: jobFranchiseId,
            }),
          };
          return { vars, opts };
        }),
        concatMap(({vars, opts}) => updateJobGQL.mutate(vars, opts).pipe(
          map((res) => JobFinancialsActions.removeDamageSuccess({ updatedJob: res.data.updateJobs.jobs[0] })),
        )),
        catchError((error) => of(JobFinancialsActions.removeDamageError({ error }))),
      )),
    ); }, { functional: true, dispatch: true });


